/* メインビジュアルの active media 同期だけを custom element 本体から分離する */
(() => {
  function registerMainVisualSyncMembers(syncMembers) {
    const registerSyncMembers = window.AuremeMainVisual?.registerSyncMembers;
    if (typeof registerSyncMembers === 'function') return registerSyncMembers(syncMembers);

    window.AuremeMainVisual = window.AuremeMainVisual || {};
    window.AuremeMainVisual.pendingSyncMembers = window.AuremeMainVisual.pendingSyncMembers || [];
    window.AuremeMainVisual.pendingSyncMembers.push(syncMembers);

    return null;
  }

  const Sync = window.AuremeMainVisual?.Sync || {};

  if (Sync.ActiveMediaSync) {
    /* section 再描画などの再評価時は、登録済み constructor をそのまま使う */
    return;
  }

  const AUTOPLAY_RETRY_MEDIA_EVENTS = ['loadedmetadata', 'loadeddata', 'canplay'];
  const AUTOPLAY_RETRY_USER_EVENTS = ['pointerup', 'touchend', 'click', 'keydown'];
  const AUTOPLAY_BLOCKED_VIDEO_ATTRIBUTE = 'data-aureme-main-visual-autoplay-blocked';
  const AUTOPLAY_BLOCKED_MEDIA_CLASS = 'aureme-main-visual__media--autoplay-blocked';
  const AUTOPLAY_BLOCKED_POSTER_VARIABLE = '--aureme-main-visual-blocked-video-poster';

  /*
   * active viewport の media だけ eager / 再生対象にし、非表示側の video は停止と先頭戻しまで同期する。
   * autoplay が一時的に拒否された場合は、active な同一 video に限って再試行する。
   */
  class ActiveMediaSync {
    constructor(element, sectionElement, breakpointQuery) {
      const runtimeSync = window.AuremeMainVisual?.Sync;
      const {
        constants: { NO_MEDIA_CLASS_BY_VIEWPORT },
        helpers: {
          getActiveMainVisualViewport,
          getActiveMainVisualViewportName,
          getActiveOrFirstMainVisualMediaElement,
          prioritizeMainVisualImage,
        },
      } = runtimeSync;

      this.element = element;
      this.sectionElement = sectionElement;
      this.breakpointQuery = breakpointQuery;
      this.noMediaClassByViewport = NO_MEDIA_CLASS_BY_VIEWPORT;
      this.getActiveMainVisualViewport = getActiveMainVisualViewport;
      this.getActiveMainVisualViewportName = getActiveMainVisualViewportName;
      this.getActiveOrFirstMainVisualMediaElement = getActiveOrFirstMainVisualMediaElement;
      this.prioritizeMainVisualImage = prioritizeMainVisualImage;
      this.autoplayRetryVideo = null;
      this.autoplayRetryCleanups = [];
    }

    sync() {
      const activeMedia = this.getActiveMedia();

      this.prioritizeActiveImage(activeMedia);
      this.syncActiveVideo(activeMedia);
      this.pauseInactiveVideos(activeMedia);
      this.playActiveVideo(activeMedia);

      return activeMedia;
    }

    disconnect() {
      this.clearAutoplayRetry();
      this.pauseInactiveVideos();
    }

    hasMediaForActiveBreakpoint() {
      if (!this.sectionElement || !this.breakpointQuery) return false;

      const viewportName = this.getActiveMainVisualViewportName(this.breakpointQuery);
      const noMediaClass = this.noMediaClassByViewport[viewportName];

      return !this.sectionElement.classList.contains(noMediaClass);
    }

    getActiveViewport() {
      return this.getActiveMainVisualViewport(this.element, this.breakpointQuery);
    }

    getActiveMedia() {
      return this.getActiveOrFirstMainVisualMediaElement(this.getActiveViewport());
    }

    prioritizeActiveImage(media) {
      /* 現在表示中のヒーロー画像だけ優先度を引き上げ、非表示側の先読みは増やさない */
      this.prioritizeMainVisualImage(media);
    }

    syncActiveVideo(media) {
      if (media?.tagName !== 'VIDEO') return;

      this.applyAutoplayAttributes(media);

      /* viewport 切替後に source の再評価が必要な時だけ load し、matching source を選び直す */
      if (media.preload !== 'auto') {
        media.preload = 'auto';
      }

      if (!media.currentSrc || media.readyState === 0) {
        media.load();
      }
    }

    applyAutoplayAttributes(media) {
      /* iOS WebKit が autoplay 判定を取りこぼさないよう、属性と property の両方を同期する */
      media.autoplay = true;
      media.muted = true;
      media.defaultMuted = true;
      media.playsInline = true;
      media.controls = false;

      if ('disablePictureInPicture' in media) {
        media.disablePictureInPicture = true;
      }

      if ('disableRemotePlayback' in media) {
        media.disableRemotePlayback = true;
      }

      media.setAttribute('autoplay', '');
      media.setAttribute('muted', '');
      media.setAttribute('playsinline', '');
      media.setAttribute('webkit-playsinline', '');
      media.setAttribute('disablepictureinpicture', '');
      media.setAttribute('disableremoteplayback', '');
      media.removeAttribute('controls');
    }

    playActiveVideo(media) {
      if (media?.tagName !== 'VIDEO') return;

      this.clearAutoplayRetry(media);

      if (!media.paused && !media.ended) {
        this.setAutoplayBlocked(media, false);
        return;
      }

      let playPromise = null;

      try {
        /* active viewport の動画だけ再生を開始する */
        playPromise = media.play();
      } catch (error) {
        this.handleAutoplayFailure(media, error);
        return;
      }

      if (!playPromise) return;

      playPromise
        .then(() => {
          this.setAutoplayBlocked(media, false);
          this.clearAutoplayRetry(media);
        })
        .catch((error) => {
          this.handleAutoplayFailure(media, error);
        });
    }

    handleAutoplayFailure(media, error) {
      if (!this.ensureAutoplayRetryVideoActive(media)) return;

      this.setAutoplayBlocked(media, true);
      console.debug('Aureme main visual autoplay was skipped.', error);
      this.scheduleAutoplayRetry(media);
    }

    scheduleAutoplayRetry(media) {
      if (this.autoplayRetryVideo === media) return;

      /*
       * autoplay がブラウザ都合で拒否された場合だけ、media の準備完了やユーザー操作を一度だけ待つ。
       * active media が切り替わった後の retry は ensureAutoplayRetryVideoActive 側で捨てる。
       */
      this.clearAutoplayRetry();
      this.autoplayRetryVideo = media;

      const retryWhenStillActive = () => {
        if (!this.ensureAutoplayRetryVideoActive(media)) return;

        this.playActiveVideo(media);
      };

      for (const eventName of AUTOPLAY_RETRY_MEDIA_EVENTS) {
        media.addEventListener(eventName, retryWhenStillActive, { once: true });
        this.autoplayRetryCleanups.push(() => {
          media.removeEventListener(eventName, retryWhenStillActive);
        });
      }

      for (const eventName of AUTOPLAY_RETRY_USER_EVENTS) {
        document.addEventListener(eventName, retryWhenStillActive, {
          capture: true,
          once: true,
          passive: true,
        });
        this.autoplayRetryCleanups.push(() => {
          document.removeEventListener(eventName, retryWhenStillActive, true);
        });
      }
    }

    ensureAutoplayRetryVideoActive(media) {
      if (this.element.isConnected && this.getActiveMedia() === media) return true;

      this.clearAutoplayRetry(media);
      return false;
    }

    clearAutoplayRetry(media = null) {
      if (media && this.autoplayRetryVideo !== media) return;

      for (const cleanup of this.autoplayRetryCleanups) {
        cleanup();
      }

      this.autoplayRetryCleanups = [];
      this.autoplayRetryVideo = null;
    }

    setAutoplayBlocked(media, isBlocked) {
      const mediaContainer = media.closest('.aureme-main-visual__media');

      media.toggleAttribute(AUTOPLAY_BLOCKED_VIDEO_ATTRIBUTE, isBlocked);
      mediaContainer?.classList.toggle(AUTOPLAY_BLOCKED_MEDIA_CLASS, isBlocked);
      this.syncAutoplayBlockedPoster(mediaContainer, media, isBlocked);
    }

    syncAutoplayBlockedPoster(mediaContainer, media, isBlocked) {
      if (!mediaContainer) return;

      if (isBlocked && media.poster) {
        mediaContainer.style.setProperty(
          AUTOPLAY_BLOCKED_POSTER_VARIABLE,
          `url("${media.poster}")`,
        );
        return;
      }

      mediaContainer.style.removeProperty(AUTOPLAY_BLOCKED_POSTER_VARIABLE);
    }

    pauseInactiveVideos(activeMedia = null) {
      for (const video of this.element.querySelectorAll('video')) {
        if (video === activeMedia) continue;
        this.clearAutoplayRetry(video);
        this.setAutoplayBlocked(video, false);
        video.pause();

        /* viewport を跨いで戻った時は先頭から再生し、途中フレームの復帰を避ける */
        if (video.currentTime !== 0) {
          video.currentTime = 0;
        }
      }
    }
  }

  registerMainVisualSyncMembers({
    ActiveMediaSync,
  });
})();
